// @ts-check

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Card, DataTable, InlineStack, Text } from "@shopify/polaris";
import { SearchResourceIcon } from "@shopify/polaris-icons";

import { TruncatedTableUTMValue } from "components/TruncatedTableUTMValue/TruncatedTableUTMValue";
import { SyncingPlaceholder } from "components/SyncingPlaceholder/SyncingPlaceholder";

import { useGlobalContext } from "context/GlobalContext";

import { useFormatCurrency } from "hooks/use-format-currency";

import { TABLE_COLUMNS } from "./utils";

import { UTM_FIELDS } from "utils/utm-sets";

const DATA_LENGTH = 5;

export const UtmSetsPreviewTable = ({ utmSets }) => {
  const navigate = useNavigate();
  const formatCurrency = useFormatCurrency();
  const { store } = useGlobalContext();

  const handleViewMore = () => {
    navigate("/utm-sets");
  };

  const data = useMemo(() => {
    return utmSets?.slice(0, DATA_LENGTH)?.map((utmSet) => {
      const formattedRevenue = formatCurrency(utmSet.total_revenue);

      const handleViewClick = () => {
        const params = {};

        for (const utmParam of UTM_FIELDS) {
          if (utmParam.key in utmSet && utmSet[utmParam.key] !== null) {
            params[utmParam.key] = utmSet[utmParam.key];
          }
        }

        navigate(`/utm-sets/view?${new URLSearchParams(params).toString()}`);
      };

      return [
        <TruncatedTableUTMValue
          value={utmSet.utm_source}
          calculateReportHref={(value) => `/reports/source/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue
          value={utmSet.utm_medium}
          calculateReportHref={(value) => `/reports/medium/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue
          value={utmSet.utm_campaign}
          calculateReportHref={(value) => `/reports/campaign/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue value={utmSet.utm_content} />,
        <TruncatedTableUTMValue value={utmSet.utm_term} />,
        <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>{utmSet.total_orders}</Text>,
        <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>{formattedRevenue}</Text>,
        <Button onClick={handleViewClick}>View UTM set</Button>
      ]
    }) || [];
  }, [utmSets, store]);

  return (
    <Card padding="0">
      <Box padding="300">
        <InlineStack align="space-between">
          <Text as="h2" variant="headingSm">Top UTM sets</Text>

          <Button
            icon={SearchResourceIcon}
            variant="tertiary"
            onClick={handleViewMore}
            accessibilityLabel="View more"
          >
            View all
          </Button>
        </InlineStack>
      </Box>

      <SyncingPlaceholder>
        <DataTable
          columnContentTypes={TABLE_COLUMNS.map(({ numeric, value }) => numeric || value === "actions" ? "numeric" : "text")}
          headings={TABLE_COLUMNS.map(({ label }) => label)}
          rows={data}
          footerContent={`Showing ${Math.min(DATA_LENGTH, data.length)} of ${utmSets?.length || 0}`}
        />
      </SyncingPlaceholder>
    </Card>
  );
};
